import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ReactPaginate from "react-paginate";
import { postData } from "../../../utility/Utility";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Swal from "sweetalert2";
import CustomLoader from "../../../Components/CustomLoader/CustomLoader";

function RoleManagementList() {
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const rowsPerPage = 10;

  // Fetch roles from API
  const fetchRoles = async () => {
    setLoading(true);
    try {
      const response = await postData("api/permissions/admin/list");
      if (response?.success) {
        // Updated to match the actual API response structure
        setRoles(response?.data?.permissions || []);
        const pageCount = Math.ceil(response?.data?.totalPermissions / rowsPerPage);
        setPageCount(pageCount);
      } else {
        toast.error(response?.message || "Failed to fetch roles");
      }
    } catch {
      toast.error("Error fetching roles");
      console.error("Fetch roles error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  // Handle page change
  const handlePageChange = ({ selected }) => {
    setCurrentPage(selected);
  };

  // Handle role deletion with SweetAlert confirmation
  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelUsedButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });
  
    if (result.isConfirmed) {
      try {
        const response = await postData('api/permissions/admin/delete', {
          permissionId:id
        });
        
        if (response.success) {
          toast.success(response.message || "Permission deleted successfully");
          fetchRoles();
        } else {
          toast.error(response.message || "Failed to delete permission");
        }
      } catch {
        toast.error("Error deleting permission");
      }
    }
  };

  return (
    <div className="content-page">
      <CustomLoader loading={loading} />
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="content">
        <div className="container-fluid">
          {/* Header Section */}
          <div className="py-3 d-flex align-items-sm-center flex-sm-row flex-column justify-content-between">
            <div className="flex-grow-1">
              <h4 className="fs-18 fw-semibold m-0">Role List</h4>
            </div>
            <div>
              <button
                className="btn btn-primary"
                onClick={() => navigate("/add-role")}
                disabled={loading}
              >
                <i className="mdi mdi-plus-square me-1"></i>
                Add Role
              </button>
            </div>
          </div>

          {/* Search and Filter Section */}
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <div className="row g-3 align-items-center">
                    <div className="col-md-8 col-lg-4">
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control ps-4"
                          placeholder="Search roles..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <span className="input-group-text">
                          <i className="mdi mdi-magnify"></i>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Roles Table */}
          <div className="row mt-2">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  {loading ? (
                    <div className="text-center py-5">
                      <CustomLoader loading={true} />
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="user-table role-management-table table-hover table-centered mb-0">
                        <thead>
                          <tr>
                            <th>S.No</th>
                            <th>User Name</th>
                            <th>User Email</th>
                            <th>User Role</th>
                            <th className="w-100">Permissions</th>
                            <th>Created At</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {roles && roles.length > 0 ? (
                            roles.map((role, index) => (
                              <tr key={role._id}>
                                <td>{index + 1}</td>
                                <td>{role?.userId?.username}</td>
                                <td>{role?.userId?.email}</td>
                                <td>{role?.userId?.role}</td>
                                <td>
                                  <div className="d-flex flex-wrap gap-2">
                                    {role?.permission?.map(p => (
                                      <div key={p._id} className="permission-badge">
                                        <span>{p.permissionName}:</span> 
                                        <span className="ms-1 mr-2">
                                          {p.actions.join(', ')}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </td>
                                <td>
                                  {new Date(
                                    role?.createdAt
                                  ).toLocaleDateString()}
                                </td>
                                <td>
                                  <div className="table-actions">
                                    <button
                                      className="btn btn-sm btn-outline-primary"
                                      onClick={() =>
                                        navigate(`/add-role`,{state: role})
                                      }
                                    >
                                      <i className="mdi mdi-pencil"></i>
                                    </button>
                                    <button
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() => handleDelete(role?._id)}
                                    >
                                      <i className="mdi mdi-delete"></i>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="8" className="text-center py-4">
                                No roles found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>

                      {/* Pagination */}
                      {pageCount > 1 && (
                        <ReactPaginate
                          previousLabel={<i className="mdi mdi-chevron-left"></i>}
                          nextLabel={<i className="mdi mdi-chevron-right"></i>}
                          breakLabel="..."
                          breakClassName="page-item"
                          breakLinkClassName="page-link"
                          pageCount={pageCount}
                          marginPagesDisplayed={2}
                          pageRangeDisplayed={5}
                          onPageChange={handlePageChange}
                          containerClassName="pagination pagination-rounded justify-content-end mt-3"
                          pageClassName="page-item"
                          pageLinkClassName="page-link"
                          previousClassName="page-item"
                          previousLinkClassName="page-link"
                          nextClassName="page-item"
                          nextLinkClassName="page-link"
                          activeClassName="active"
                          forcePage={currentPage}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RoleManagementList;